// src/interfaces/user/UserInterface.js
import React, { useState, useEffect } from 'react';
import Navigation from './Navigation';
import PriceCalculator from './PriceCalculator';
import QuotesList from './QuotesList';
import SmallFormatCalculator from './SmallFormatCalculator';
import DraftsList from './DraftsList';
import AddressBookPage from './AddressBookPage';
import { useQuotes } from '../../context/QuoteContext';
import { useAuth } from '../../context/AuthContext';
import usePersistedState from '../../hooks/usePersistedState'; // Import the custom hook

const UserInterface = () => {
  const { currentUser, hasRole } = useAuth();
  
  // Replace useState with usePersistedState for page and job type persistence
  const [activePage, setActivePage] = usePersistedState(
    'userInterface_activePage',
    'calculator',
    (value) => ['calculator', 'drafts', 'quotes', 'address-book'].includes(value) // Updated validator
  );
  
  const [jobType, setJobType] = usePersistedState(
    'userInterface_jobType',
    'small-format',
    (value) => ['large-format', 'small-format'].includes(value) // Validator function
  );
  
  // Pricing tier from user account (users cannot change this)
  const pricingTier = currentUser?.pricing_tier || 'retail';
  
  const {
    targetCalculator,
    clearLoadedQuote,
    fetchQuotesWithContext,
    fetchDraftsWithContext,
    pendingUploadQuoteId
  } = useQuotes();
  

  useEffect(() => {
    // If a specific calculator type is targeted (from "Edit Similar" or quote loading)
    if (targetCalculator) {
      // Ensure we're on the calculator page
      if (activePage !== 'calculator') {
        setActivePage('calculator');
      }
      // Set the jobType to match the targeted calculator
      if (jobType !== targetCalculator) {
        setJobType(targetCalculator);
      }
      // The calculators themselves will use `quoteToLoad` and `targetCalculator`
      // and should call `clearLoadedQuote()` after they have consumed the data.
    }
  }, [targetCalculator, activePage, jobType, setActivePage, setJobType]);

  // If a quote submission queued an upload, automatically jump to quotes list
  useEffect(() => {
    if (pendingUploadQuoteId && activePage !== 'quotes') {
      setActivePage('quotes');
    }
  }, [pendingUploadQuoteId, activePage, setActivePage]);

  // Fetch quotes and drafts with management context
  useEffect(() => {
    if (activePage === 'quotes' || activePage === 'drafts') {
      fetchQuotesWithContext('management');
      fetchDraftsWithContext('management');
    }
  }, [activePage, fetchQuotesWithContext, fetchDraftsWithContext]);
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-sky-50 to-slate-200">
      <Navigation
        activePage={activePage}
        setActivePage={setActivePage}
        jobType={jobType}
        setJobType={setJobType}
      />
      
      <div className={`container mx-auto px-4 ${activePage === 'calculator' ? 'pb-6 pt-4' : 'py-6'}`}>
        <main>
          {activePage === 'calculator' && jobType === 'large-format' && (
            <PriceCalculator pricingTier={pricingTier} />
          )}
          {activePage === 'calculator' && jobType === 'small-format' && (
            <SmallFormatCalculator pricingTier={pricingTier} />
          )}
          {activePage === 'drafts' && <DraftsList setActivePage={setActivePage} />}
          {activePage === 'quotes' && <QuotesList setActivePage={setActivePage} />}
          {activePage === 'address-book' && <AddressBookPage />}
        </main>
      </div>
    </div>
  );
};

export default UserInterface;
